import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../lib/api';
import type { CaseSummary, ClientSummary } from '../types';

/**
 * Pre-upload dialog: pick client → pick case (or "no case") → continue.
 * On continue, calls onConfirm with the chosen ids; the parent then opens
 * the OS file picker. Decoupling these two steps lets the user back out
 * before touching their filesystem.
 */
export function UploadDocumentDialog({
  onConfirm,
  onClose,
}: {
  onConfirm: (opts: { clientId: string; caseId: string | null }) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation(['documents', 'clients', 'cases', 'common']);
  const [clients, setClients] = useState<ClientSummary[]>([]);
  const [cases, setCases] = useState<CaseSummary[]>([]);
  const [clientId, setClientId] = useState('');
  const [caseId, setCaseId] = useState('');
  const [loadingCases, setLoadingCases] = useState(false);

  useEffect(() => {
    apiFetch<{ items: ClientSummary[] }>('/api/clients')
      .then((d) => setClients(d.items))
      .catch(() => setClients([]));
  }, []);

  useEffect(() => {
    if (!clientId) {
      setCases([]);
      setCaseId('');
      return;
    }
    setLoadingCases(true);
    apiFetch<{ items: CaseSummary[] }>(`/api/cases?clientId=${clientId}`)
      .then((d) => setCases(d.items))
      .catch(() => setCases([]))
      .finally(() => setLoadingCases(false));
    setCaseId('');
  }, [clientId]);

  function handleContinue() {
    if (!clientId) return;
    onConfirm({ clientId, caseId: caseId || null });
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="form-card modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="form-card-header">
          <div style={{ flex: 1 }}>
            <div className="form-preview-name">
              {t('documents:upload_dialog.title')}
            </div>
            <div className="form-preview-type">
              {t('documents:upload_dialog.subtitle')}
            </div>
          </div>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            aria-label={t('common:actions.close')}
          >
            ×
          </button>
        </div>

        <div className="form-section">
          <div className="field-grid" style={{ gridTemplateColumns: '1fr' }}>
            <div className="field">
              <label>
                {t('documents:upload_dialog.client')} <span className="req">*</span>
              </label>
              <select value={clientId} onChange={(e) => setClientId(e.target.value)} required>
                <option value="">{t('documents:upload_dialog.pick_client')}</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label>{t('documents:upload_dialog.case')}</label>
              <select
                value={caseId}
                onChange={(e) => setCaseId(e.target.value)}
                disabled={!clientId || loadingCases}
              >
                <option value="">
                  {!clientId
                    ? t('documents:upload_dialog.pick_client_first')
                    : loadingCases
                      ? t('documents:upload_dialog.loading_cases')
                      : t('documents:upload_dialog.no_case')}
                </option>
                {cases.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.caseNumber}
                    {c.area ? ` · ${c.area}` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="btn-cancel" onClick={onClose}>
            {t('common:actions.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleContinue}
            disabled={!clientId}
          >
            {t('documents:upload_dialog.continue')}
          </button>
        </div>
      </div>
    </div>
  );
}
